import { useMemo, type CSSProperties, type ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';
import { UnifiedLiquidityEnum, type Security } from '../../types';
import { isDeribitLinearOption } from '../../utils';
import type { BoxProps } from '../Core';
import { CurrencyIcon } from '../CurrencyIcon';
import { Tooltip } from '../Tooltip';
import { Currency, DisplaySecurityWrapper, Wrapper } from './styles';

const TARGET_STYLES: CSSProperties = { textAlign: 'center', display: 'inline-block', overflow: 'hidden' };

export const CurrencyPair = styled(
  ({
    security,
    quoteCurrencies,
    unifiedLiquidity,
    unifiedLiquidityIndicator,
    showDisplaySymbol,
    ...props
  }: {
    security?: Security;
    quoteCurrencies?: string[];
    unifiedLiquidity?: UnifiedLiquidityEnum;
    unifiedLiquidityIndicator?: ReactNode;
    showDisplaySymbol?: boolean;
    style?: CSSProperties;
  } & Omit<BoxProps, 'security'>) => {
    if (security == null) {
      return null;
    }
    const showUnderlyingSymbols = unifiedLiquidity === UnifiedLiquidityEnum.Enabled;
    return (
      <Tooltip
        tooltip={security.Description ? <div>{security.Description}</div> : null}
        disabled={showUnderlyingSymbols}
        targetStyle={TARGET_STYLES}
      >
        {showDisplaySymbol ? (
          <DisplaySecurityWrapper {...props}>{security.DisplaySymbol}</DisplaySecurityWrapper>
        ) : (
          <Wrapper {...props}>
            <CurrencyPairPart security={security} currency={security.BaseCurrency ?? ''} />
            <CurrencyPairPart
              security={security}
              currency={security.QuoteCurrency}
              currencies={quoteCurrencies}
              unifiedLiquidity={unifiedLiquidity}
              unifiedLiquidityIndicator={unifiedLiquidityIndicator}
            />
          </Wrapper>
        )}
      </Tooltip>
    );
  }
)``;

export const CurrencyPairPart = ({
  security,
  currency,
  currencies,
  unifiedLiquidity,
  unifiedLiquidityIndicator,
}: {
  security: Security;
  currency: string;
  currencies?: string[];
  unifiedLiquidity?: UnifiedLiquidityEnum;
  unifiedLiquidityIndicator?: ReactNode;
}) => {
  const { fontSizeDefault, baseSize } = useTheme();
  const showUnifiedLiquidity = unifiedLiquidity === UnifiedLiquidityEnum.Enabled;

  // https://talostrading.atlassian.net/browse/UI-4062
  const currencyStr = useMemo(() => {
    return isDeribitLinearOption(security) ? [security.BaseCurrency, security.QuoteCurrency].join(' ') : currency;
  }, [security, currency]);

  return (
    <Currency>
      <CurrencyIcon stacked={showUnifiedLiquidity} currency={currency} size={fontSizeDefault * baseSize} />
      <span>
        {currencyStr}
        {unifiedLiquidityIndicator}
      </span>
    </Currency>
  );
};
